/**
 * AdjacencyLines — renders chalk-stroke connectors between adjacent territory
 * centroids for the active map. Each line is a quadratic Bezier with a
 * mid-point jitter (deterministic per edge) so connectors never look ruler-
 * straight, mirroring the 80 Days route map reference.
 *
 * Lines render under the territory paths in MapView (Z-order matters: the
 * MapView places <AdjacencyLines/> before the territory `<g>`). The component
 * tries to apply the global chalk filter; if the page hasn't mounted that
 * filter yet, browsers gracefully fall back to a flat stroke.
 */

import { type ReactNode } from 'react';
import { TERRITORY_TO_CONTINENT, type ActiveMap, type Territory, type TerritoryId } from '@qd/map';
import { surfaces } from '@qd/design-tokens';
import { getOffsetCentroid } from './MapOffsets.js';

interface AdjacencyLinesProps {
  readonly map: ActiveMap;
}

/** SVG filter id; see Phase 1 SvgFilters scaffold. Falls back gracefully if absent. */
const CHALK_FILTER_ID = 'qd-filter-chalk-roughen';

export function AdjacencyLines({ map }: AdjacencyLinesProps): ReactNode {
  const seen = new Set<string>();
  const edges: { from: Territory; to: Territory; sea: boolean }[] = [];
  const byId = new Map<TerritoryId, Territory>(map.territories.map((t) => [t.id, t] as const));

  for (const territory of map.territories) {
    for (const neighbourId of map.adjacency.get(territory.id) ?? []) {
      const key = territory.id < neighbourId ? `${territory.id}|${neighbourId}` : `${neighbourId}|${territory.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const partner = byId.get(neighbourId);
      if (!partner) continue;
      const fromCont = TERRITORY_TO_CONTINENT[territory.id];
      const toCont = TERRITORY_TO_CONTINENT[partner.id];
      const sea = fromCont !== undefined && toCont !== undefined && fromCont !== toCont;
      edges.push({ from: territory, to: partner, sea });
    }
  }

  // Land borders: very faint chalk dots — they sit beneath territory polygons
  // so they only peek through at the seams. Sea routes: bolder dashed
  // shipping lanes that arc across the slate ocean.
  const landEdges = edges.filter((e) => !e.sea);
  const seaEdges = edges.filter((e) => e.sea);

  return (
    <>
      {/* Land border ghosts — drawn under everything, very faint. */}
      <g
        data-layer="adjacency-land"
        stroke={surfaces.chalk}
        strokeOpacity={0.12}
        strokeWidth={0.8}
        fill="none"
        pointerEvents="none"
      >
        {landEdges.map(({ from, to }) => (
          <path
            key={`${from.id}__${to.id}`}
            d={curveBetween(from, to)}
            strokeLinecap="round"
            strokeDasharray="1 4"
          />
        ))}
      </g>

      {/* Inter-continent shipping routes — the iconic Risk dashed sea lane. */}
      <g
        data-layer="adjacency-sea"
        filter={`url(#${CHALK_FILTER_ID})`}
        stroke={surfaces.chalk}
        strokeOpacity={0.65}
        strokeWidth={2}
        fill="none"
        pointerEvents="none"
      >
        <defs>
          <marker id="sea-route-arrow" viewBox="0 0 10 10" refX="8" refY="5"
              markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={surfaces.chalk} />
          </marker>
        </defs>
        {seaEdges.map(({ from, to }) => {
          const fromOff = getOffsetCentroid(from.id, from.centroid);
          const toOff = getOffsetCentroid(to.id, to.centroid);
          return (
            <g key={`${from.id}__${to.id}`}>
              <path
                d={curveBetween(from, to)}
                strokeLinecap="round"
                strokeDasharray="6 8"
                markerEnd="url(#sea-route-arrow)"
              />
              {/* Endpoint pegs so each lane reads as deliberate, not a stray line. */}
              <circle
                cx={fromOff.x}
                cy={fromOff.y}
                r={2}
                fill={surfaces.chalk}
                fillOpacity={0.8}
                stroke="none"
              />
              <circle
                cx={toOff.x}
                cy={toOff.y}
                r={2}
                fill={surfaces.chalk}
                fillOpacity={0.8}
                stroke="none"
              />
            </g>
          );
        })}
      </g>
    </>
  );
}

function curveBetween(a: Territory, b: Territory): string {
  const aCent = getOffsetCentroid(a.id, a.centroid);
  const bCent = getOffsetCentroid(b.id, b.centroid);
  
  const dx = bCent.x - aCent.x;
  const dy = bCent.y - aCent.y;
  const length = Math.hypot(dx, dy) || 1;

  // Unit perpendicular (rotated 90°, sign chosen by hash so edges don't all
  // bow the same way).
  const perpX = -dy / length;
  const perpY = dx / length;

  const seed = simpleHash(a.id + '__' + b.id);
  const sign = seed % 2 === 0 ? 1 : -1;
  const magnitude = Math.min(40, length * 0.15); // increased bowing

  const midX = (aCent.x + bCent.x) / 2 + perpX * magnitude * sign;
  const midY = (aCent.y + bCent.y) / 2 + perpY * magnitude * sign;

  return `M${aCent.x},${aCent.y} Q${midX.toFixed(1)},${midY.toFixed(1)} ${bCent.x},${bCent.y}`;
}

/** Tiny non-cryptographic hash so per-edge curvature is deterministic. */
function simpleHash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
